import { useFirebase } from 'contexts/FirebaseContext';
import dayjs from 'dayjs';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { EventOccasion } from 'models/EventOccasion';
import { useEffect, useState } from 'react';
import { isElectron } from 'utils/isElectron';
import { uuid } from 'utils/uuid';
import { useUser } from './useUser';

export function useEvent(eventId: string, initialEvent?: EventOccasion) {
  const [event, setEvent] = useState<EventOccasion | undefined>(initialEvent);
  const { user } = useUser();
  const db = useFirebase().db;

  const createVipKey = () => {
    return uuid().split('-')[0];
  };

  const getEventUrl = (isVip = false) => {
    if (!user) return '';

    const effectiveEventId = eventId || event?.id;
    if (!effectiveEventId) return '';

    const isLive = event?.isLiveEvent ?? initialEvent?.isLiveEvent;
    const eventPath = isLive
      ? `/${user.uid}/events/${effectiveEventId}/live-request`
      : `/${user.uid}/events/${effectiveEventId}/request`;

    const baseUrl = isElectron() ? process.env.REACT_APP_BASE_URL : window.location.origin;

    const url = `${baseUrl}${eventPath}`;

    const vipKey = event?.vipKey ?? initialEvent?.vipKey;
    if (isVip && vipKey) {
      return `${url}?vp=${vipKey}`;
    }

    return url;
  };

  const loadEvent = async () => {
    if (eventId) {
      const eventRef = doc(db, `user/${user?.uid}/events`, eventId);
      const eventSnapshot = await getDoc(eventRef);

      if (eventSnapshot.exists()) {
        setEvent({ ...eventSnapshot.data(), ...{ id: eventId } } as EventOccasion);
      }
    }
  };

  const updateEvent = async (values: Partial<EventOccasion>) => {
    const newEvent = { ...event, ...values } as EventOccasion;

    if (newEvent.date && (event?.expireDate || newEvent.date)) {
      const expireDate = newEvent.expireDate ?? event?.expireDate;
      if (newEvent.date && dayjs(newEvent.date).isBefore(dayjs(expireDate))) {
        newEvent.expireDate = newEvent.date;
      }
    }

    if (eventId && user) {
      await setDoc(doc(db, `user/${user.uid}/events`, eventId), newEvent, { merge: true });
      setEvent(newEvent);
    }
  };

  useEffect(() => {
    if (!user || !eventId) return;

    const fetchEvent = async () => {
      await loadEvent();
    };

    fetchEvent();
  }, [db, user, eventId]);

  return {
    event,
    updateEvent,
    loadEvent,
    getEventUrl,
    createVipKey,
    eventIsExpired: event?.expireDate ? dayjs().isAfter(dayjs(event.expireDate)) : false,
  };
}
